import type { App, Project } from '@hangar/shared';
import { useProfiles, useProviderAccounts } from '@/api/hooks';
import type { Profile, ProviderAccount } from '@/api/schemas';
import { stagger } from '@/lib/motion';
import { useBreakpoint } from '@/lib/useBreakpoint';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { listView } from '@/features/env/envHelpers';
import { ProfilesDesktopTable } from './ProfilesDesktopTable';
import { ProfilesPhoneList } from './ProfilesPhoneList';
import { useProfileActions, type ProfileActions } from './useProfileActions';

/**
 * Screen 09 "Build profiles" of the selected app: table (cards on phones) with Run, Edit, Duplicate and Archive, and the
 * create sheet. Run follows the API's capability flags (`me.features`); writes follow the caller's role. "Sync from
 * hangar.yml" (ConfigSyncDialog) returns with the hangar.yml sync of docs/mvp-plan.md P9, deferred.
 */
export function ProfilesPanel({ app, project }: { app: App; project: Project }) {
  const profiles = useProfiles(app.id);
  const accounts = useProviderAccounts();
  const actions = useProfileActions({ app, project, accounts: accounts.data });
  return (
    <Card className="enter" style={stagger(2)} data-testid="profiles-panel">
      <PanelHeader count={profiles.data?.length} actions={actions} />
      <CardContent className="p-0 sm:p-0">
        <ProfilesBody query={profiles} app={app} project={project} accounts={accounts.data} actions={actions} />
      </CardContent>
      {actions.overlays}
    </Card>
  );
}

/** "New profile" is outline (here and in the empty state): Connect app or Edit settings is the page's one primary action. */
function PanelHeader({ count, actions }: { count: number | undefined; actions: ProfileActions }) {
  return (
    <CardHeader>
      <CardTitle>Build profiles{count !== undefined ? ` (${count})` : ''}</CardTitle>
      <CardDescription>How each build of this app is made: Android or iOS, its environments and what it produces</CardDescription>
      <CardAction className="flex-wrap">
        <GatedButton variant="outline" data-testid="profiles-new" can={actions.can} onClick={actions.create}>
          <Icon name="add" size={18} />
          New profile
        </GatedButton>
      </CardAction>
    </CardHeader>
  );
}

interface BodyProps {
  query: ReturnType<typeof useProfiles>;
  app: App;
  project: Project;
  accounts: ProviderAccount[] | undefined;
  actions: ProfileActions;
}

function ProfilesBody({ query, app, project, accounts, actions }: BodyProps) {
  const bp = useBreakpoint();
  const view = listView({ isLoading: query.isLoading, isError: query.isError, hasData: Boolean(query.data), count: query.data?.length ?? 0 });
  if (view === 'loading') return <ProfilesSkeleton />;
  if (view === 'error') return <ErrorState compact error={query.error} onRetry={() => void query.refetch()} />;
  if (view === 'empty' || !query.data) return <NoProfiles app={app} actions={actions} />;
  const list: Profile[] = query.data;
  const props = { profiles: list, app, project, accounts, actionsFor: actions.actionsFor };
  return bp === 'phone' ? <ProfilesPhoneList {...props} /> : <ProfilesDesktopTable {...props} />;
}

function ProfilesSkeleton() {
  return (
    <div className="flex flex-col gap-2 p-4" aria-busy="true">
      {[0, 1].map((i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

function NoProfiles({ app, actions }: { app: App; actions: ProfileActions }) {
  return (
    <EmptyState
      testId="profiles-empty"
      icon="tune"
      title="No build profiles"
      description={`A profile says how ${app.name} is built: Android or iOS, the environments it may build for, and what each build produces.`}
      action={
        <GatedButton variant="outline" data-testid="profiles-empty-new" can={actions.can} onClick={actions.create}>
          <Icon name="add" size={18} />
          New profile
        </GatedButton>
      }
    />
  );
}
